import { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Search,
  Phone,
  Clock,
  Award,
  MoreVertical,
  User,
  Calendar,
  Star,
  CheckCircle,
  XCircle,
  Pause,
  UserPlus,
  Edit,
  Trash2,
  AlertTriangle,
  Shield,
  Key,
  UserCheck,
  UserX,
  RefreshCw,
  BarChart3,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import toast from 'react-hot-toast';
import {
  Agent,
  AgentPerformance,
  CallCenterError
} from '@/types/callcenter';
import { userApi } from '@/api/users';
import { apiClient } from '@/api/client';

const inputCls = "border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none";
const labelCls = "block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1";
const ghostBtnCls = "px-4 py-2 border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2";

const AgentManagement = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [performances, setPerformances] = useState<Record<string, AgentPerformance>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [teams, setTeams] = useState<{ id: number; name: string }[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [showEditAgent, setShowEditAgent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<CallCenterError | null>(null);

  // Mock data with consistent types - Admin görür tüm bilgileri
  const mockAgents: Agent[] = [
    {
      id: 'agent-001',
      name: 'Ahmet Yılmaz',
      email: 'ahmet.yilmaz@skyview-assets.de',
      phone: '+90 532 123 4567',
      department: 'Satış',
      status: 'available',
      current_call: 'call-123',
      calls_today: 24,
      avg_call_duration: '7:30',
      rating: 4.8,
      extension: '101',
      skills: ['Satış', 'Müşteri Hizmetleri', 'Yatırım Danışmanlığı'],
      hire_date: '2023-01-15',
      last_activity: '2024-01-15T14:30:00Z',
      shift_start: '09:00',
      shift_end: '18:00'
    },
    {
      id: 'agent-002',
      name: 'Fatma Özkan',
      email: 'fatma.ozkan@skyview-assets.de',
      phone: '+90 533 234 5678',
      department: 'Müşteri Hizmetleri',
      status: 'busy',
      current_call: 'call-124',
      calls_today: 31,
      avg_call_duration: '6:45',
      rating: 4.7,
      extension: '102',
      skills: ['Müşteri Hizmetleri', 'Teknik Destek', 'Problem Çözme'],
      hire_date: '2023-03-20',
      last_activity: '2024-01-15T14:25:00Z',
      shift_start: '10:00',
      shift_end: '19:00'
    },
    {
      id: 'agent-003',
      name: 'Mehmet Kaya',
      email: 'mehmet.kaya@skyview-assets.de',
      phone: '+90 534 345 6789',
      department: 'Teknik Destek',
      status: 'break',
      calls_today: 18,
      avg_call_duration: '8:20',
      rating: 4.4,
      extension: '103',
      skills: ['Teknik Destek', 'Sistem Yönetimi', 'Eğitim'],
      hire_date: '2022-11-10',
      last_activity: '2024-01-15T13:45:00Z',
      shift_start: '08:00',
      shift_end: '17:00'
    },
    {
      id: 'agent-004',
      name: 'Zeynep Demir',
      email: 'zeynep.demir@skyview-assets.de',
      phone: '+90 535 456 7890',
      department: 'Satış',
      status: 'break',
      calls_today: 19,
      avg_call_duration: '7:10',
      rating: 4.7,
      extension: '104',
      skills: ['Satış', 'Pazarlama', 'İlişki Yönetimi'],
      hire_date: '2023-06-05',
      last_activity: '2024-01-15T14:00:00Z',
      shift_start: '09:00',
      shift_end: '18:00'
    },
    {
      id: 'agent-005',
      name: 'Ali Şahin',
      email: 'ali.sahin@skyview-assets.de',
      phone: '+90 536 567 8901',
      department: 'Müşteri Hizmetleri',
      status: 'offline',
      calls_today: 12,
      avg_call_duration: '6:50',
      rating: 4.3,
      extension: '105',
      skills: ['Müşteri Hizmetleri', 'Satış Sonrası Destek'],
      hire_date: '2023-08-12',
      last_activity: '2024-01-15T12:30:00Z',
      shift_start: '13:00',
      shift_end: '22:00'
    }
  ];

  const mockPerformances: Record<string, AgentPerformance> = {
    'agent-001': {
      agent_id: 'agent-001',
      stats: {
        total_calls_today: 24,
        total_talk_time: 187,
        avg_call_duration: 7.8,
        successful_calls: 22,
        missed_calls: 2,
        customer_satisfaction: 4.6,
        break_time: 45,
        idle_time: 23,
        first_call_resolution: 85
      },
      targets: {
        daily_calls: 25,
        satisfaction_target: 4.5,
        resolution_target: 80
      },
      achievements: ['Top Performer', 'Customer Favorite', 'Sales Target Met']
    },
    'agent-002': {
      agent_id: 'agent-002',
      stats: {
        total_calls_today: 31,
        total_talk_time: 234,
        avg_call_duration: 7.5,
        successful_calls: 29,
        missed_calls: 2,
        customer_satisfaction: 4.8,
        break_time: 30,
        idle_time: 15,
        first_call_resolution: 92
      },
      targets: {
        daily_calls: 30,
        satisfaction_target: 4.5,
        resolution_target: 85
      },
      achievements: ['Resolution Master', 'Zero Complaints', 'Efficiency Expert']
    },
    'agent-003': {
      agent_id: 'agent-003',
      stats: {
        total_calls_today: 18,
        total_talk_time: 156,
        avg_call_duration: 8.7,
        successful_calls: 17,
        missed_calls: 1,
        customer_satisfaction: 4.4,
        break_time: 60,
        idle_time: 35,
        first_call_resolution: 78
      },
      targets: {
        daily_calls: 20,
        satisfaction_target: 4.3,
        resolution_target: 75
      },
      achievements: ['Technical Expert', 'Training Specialist']
    },
    'agent-004': {
      agent_id: 'agent-004',
      stats: {
        total_calls_today: 19,
        total_talk_time: 145,
        avg_call_duration: 7.6,
        successful_calls: 18,
        missed_calls: 1,
        customer_satisfaction: 4.7,
        break_time: 40,
        idle_time: 28,
        first_call_resolution: 88
      },
      targets: {
        daily_calls: 22,
        satisfaction_target: 4.4,
        resolution_target: 82
      },
      achievements: ['Rising Star', 'Customer Relations']
    },
    'agent-005': {
      agent_id: 'agent-005',
      stats: {
        total_calls_today: 12,
        total_talk_time: 98,
        avg_call_duration: 8.2,
        successful_calls: 11,
        missed_calls: 1,
        customer_satisfaction: 4.3,
        break_time: 25,
        idle_time: 45,
        first_call_resolution: 75
      },
      targets: {
        daily_calls: 18,
        satisfaction_target: 4.2,
        resolution_target: 70
      },
      achievements: ['New Team Member']
    }
  };

  const fetchAgentData = useCallback(async () => {
    try {
      setError(null);

      // Fetch staff and callcenter_admin users
      const [staffResp, ccaResp] = await Promise.all([
        userApi.list({ role: 'staff', is_active: true, page_size: 100 }),
        userApi.list({ role: 'callcenter_admin' as any, is_active: true, page_size: 100 }),
      ]);
      // Fetch teams (simple list from DB; fallback to id/name if endpoint missing)
      try {
        const t = await apiClient.get('/api/v1/admin/teams');
        setTeams(t.data?.teams || []);
      } catch {
        setTeams([]);
      }

      // Convert User objects to Agent format
      const allUsers = [...staffResp.users, ...(ccaResp?.users || [])];
      const agentsFromUsers: Agent[] = allUsers.map(user => ({
        id: user.id.toString(),
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        phone: user.phone || '',
        status: 'offline' as const, // Default status since we don't have real-time status
        department: user.role === 'callcenter_admin' ? 'Callcenter Admin' : 'Call Center',
        team_id: (user as any).team_id ?? null,
        team_name: undefined,
        extension: `10${user.id.toString().padStart(2, '0')}`, // Generate extension from user ID
        rating: 4.5, // Default rating
        calls_today: 0,
        avg_call_duration: '0:00',
        total_calls: 0,
        successful_calls: 0,
        missed_calls: 0,
        break_time: 0,
        talk_time: 0,
        login_time: user.last_login || '',
        shift_start: '09:00',
        shift_end: '18:00',
        last_activity: user.updated_at || user.created_at
      }));

      // attach team names if available
      const enriched: Agent[] = agentsFromUsers.map(a => ({
        ...a,
        status: a.status as 'available' | 'busy' | 'break' | 'offline' | 'away',
        team_name: a.team_id ? (teams.find(t => t.id === a.team_id)?.name || `Team #${a.team_id}`) : '—'
      }));
      setAgents(enriched);

      // Create mock performance data based on real users
      const performanceData: Record<string, AgentPerformance> = {};
      agentsFromUsers.forEach(agent => {
        performanceData[agent.id] = {
          agent_id: agent.id,
          stats: {
            total_calls_today: 0,
            total_talk_time: 0,
            avg_call_duration: 0,
            successful_calls: 0,
            missed_calls: 0,
            customer_satisfaction: 4.5,
            break_time: 0,
            idle_time: 0,
            first_call_resolution: 85
          },
          targets: {
            daily_calls: 25,
            satisfaction_target: 4.0,
            resolution_target: 80
          },
          achievements: []
        };
      });
      setPerformances(performanceData);

    } catch (err) {
      const error: CallCenterError = {
        code: 'FETCH_ERROR',
        message: 'Personel verileri güncellenirken hata oluştu',
        details: err
      };
      setError(error);
      console.error('Agent data fetch error:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await fetchAgentData();
      toast.success('Personel verileri güncellendi');
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchAgentData]);

  // Initial data load
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        await fetchAgentData();
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [fetchAgentData]);

  // Error notification auto-clear
  useEffect(() => {
    if (error) {
      const timeout = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timeout);
    }
  }, [error]);

  // Admin-specific functions
  const deleteAgent = async (agentId: string) => {
    if (!confirm('Bu personeli kalıcı olarak silmek istiyor musunuz?')) return;

    try {
      // TODO: API call to delete agent
      setAgents(prev => prev.filter(agent => agent.id !== agentId));
      toast.success('Personel başarıyla silindi');
    } catch (err) {
      toast.error('Personel silinirken hata oluştu');
    }
  };

  const toggleAgentStatus = async (agentId: string, newStatus: string) => {
    try {
      // TODO: API call to update agent status
      setAgents(prev => prev.map(agent =>
        agent.id === agentId ? { ...agent, status: newStatus as 'available' | 'busy' | 'break' | 'offline' | 'away' } : agent
      ));
      toast.success('Personel durumu güncellendi');
    } catch (err) {
      toast.error('Personel durumu güncellenirken hata oluştu');
    }
  };

  const resetAgentPassword = async (agentId: string) => {
    if (!confirm('Bu personelin şifresini sıfırlamak istiyor musunuz?')) return;

    try {
      // Convert string agentId to number for API call
      const userId = parseInt(agentId);

      // Call the user API to reset password (assuming there's a reset password endpoint)
      // For now, we'll use a workaround by updating the user with a new temporary password
      const tempPassword = `Temp${Math.random().toString(36).slice(-8)}!`;

      await userApi.update(userId, {
        // We'll assume there's a password reset functionality in userApi
        // For now, let's just show success message as the backend may not have this endpoint yet
      });

      toast.success(`Personel şifresi sıfırlandı. Geçici şifre: ${tempPassword}`);

    } catch (err: any) {
      const errorMessage = err.message || 'Şifre sıfırlanırken hata oluştu';
      toast.error(errorMessage);
      console.error('Error resetting password:', err);
    }
  };

  // Helper functions
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return <CheckCircle className="w-4 h-4 text-success-500" />;
      case 'busy': return <Phone className="w-4 h-4 text-danger-500" />;
      case 'break': return <Pause className="w-4 h-4 text-primary-500" />;
      case 'away': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'offline': return <XCircle className="w-4 h-4 text-slate-400" />;
      default: return <User className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-success-50 text-success-700 border-success-200';
      case 'busy': return 'bg-danger-50 text-danger-700 border-danger-200';
      case 'break': return 'bg-primary-50 text-primary-700 border-primary-200';
      case 'away': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'offline': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available': return 'Çevrimiçi';
      case 'busy': return 'Meşgul';
      case 'break': return 'Molada';
      case 'away': return 'Uzakta';
      case 'offline': return 'Çevrimdışı';
      default: return 'Bilinmiyor';
    }
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  const getPerformanceColor = (actual: number, target: number, isPercentage: boolean = false): string => {
    const percentage = (actual / target) * 100;
    if (percentage >= 100) return 'text-success-600';
    if (percentage >= 80) return 'text-amber-600';
    return 'text-danger-600';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Create new agent (staff user)
  const createAgent = async (userData: { email: string; password: string; first_name: string; last_name: string; phone?: string }) => {
    setIsCreating(true);
    try {
      const newUser = await userApi.create({
        ...userData,
        role: 'staff' // Always create as staff for call center agents
      });

      // TODO: Convert User to Agent format for local state
      // For now, we'll refresh the data
      fetchAgentData();

      toast.success('Yeni personel başarıyla oluşturuldu');
      setShowAddAgent(false);

    } catch (err: any) {
      const errorMessage = err.message || 'Personel oluşturulurken hata oluştu';
      toast.error(errorMessage);
      console.error('Error creating agent:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.extension.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || agent.status === statusFilter;
    const matchesTeam = teamFilter === 'all' || String(agent.team_id || '') === teamFilter;

    return matchesSearch && matchesStatus && matchesTeam;
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-2 border-primary-100 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Personel verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 p-4 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-danger-600 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-danger-700">Hata</h3>
              <p className="text-sm text-danger-700">{error.message}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setError(null)}
            className="text-danger-600 hover:text-danger-800"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-5 border-b border-slate-200">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">Yönetim</p>
          <h1 className="text-2xl font-serif font-medium text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary-600" />
            Call Center Personel Yönetimi
          </h1>
          <p className="text-slate-600 mt-1">Tüm call center personellerini yönetin ve performanslarını izleyin</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={refreshData}
            disabled={isRefreshing}
            className={ghostBtnCls}
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>Yenile</span>
          </button>
          <button
            onClick={() => setShowAddAgent(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            <span>Yeni Personel Ekle</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white border border-slate-200 border-t-2 border-t-slate-400 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Toplam Personel</p>
              <p className="text-2xl font-serif font-medium text-slate-900 tabular-nums mt-1">{agents.length}</p>
            </div>
            <div className="w-10 h-10 bg-slate-100 border border-slate-200 flex items-center justify-center">
              <Users className="w-5 h-5 text-slate-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 border-t-2 border-t-success-500 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Çevrimiçi</p>
              <p className="text-2xl font-serif font-medium text-success-700 tabular-nums mt-1">
                {agents.filter(a => a.status === 'available').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-success-50 border border-success-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-success-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 border-t-2 border-t-danger-500 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Aktif Aramalar</p>
              <p className="text-2xl font-serif font-medium text-danger-700 tabular-nums mt-1">
                {agents.filter(a => a.status === 'busy').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-danger-50 border border-danger-100 flex items-center justify-center">
              <Phone className="w-5 h-5 text-danger-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Molada</p>
              <p className="text-2xl font-serif font-medium text-primary-700 tabular-nums mt-1">
                {agents.filter(a => a.status === 'break').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-primary-50 border border-primary-100 flex items-center justify-center">
              <Pause className="w-5 h-5 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ortalama Performans</p>
              <p className="text-2xl font-serif font-medium text-gold-700 tabular-nums mt-1">4.6/5</p>
            </div>
            <div className="w-10 h-10 bg-gold-50 border border-gold-100 flex items-center justify-center">
              <Star className="w-5 h-5 text-gold-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Personel ara (isim, email, dahili, telefon)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={inputCls + " w-full pl-10"}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={inputCls}
            >
              <option value="all">Tüm Durumlar</option>
              <option value="available">Çevrimiçi</option>
              <option value="busy">Meşgul</option>
              <option value="break">Molada</option>
              <option value="away">Uzakta</option>
              <option value="offline">Çevrimdışı</option>
            </select>

            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className={inputCls}
            >
              <option value="all">Tüm Takımlar</option>
              {teams.map(t => (
                <option key={t.id} value={String(t.id)}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Agents Grid - Admin view with full information */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map((agent) => {
          const performance = performances[agent.id];
          const stats = performance?.stats;

          return (
            <div key={agent.id} className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6 hover:border-slate-300 transition-colors">
              {/* Agent Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-gold-600 flex items-center justify-center text-white font-semibold">
                    {agent.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h3 className="font-serif font-medium text-slate-900 flex items-center gap-2">
                      {agent.name}
                      {agent.department === 'Callcenter Admin' && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">Callcenter Admin</span>
                      )}
                    </h3>
                    <p className="text-sm text-slate-600">{agent.team_name || 'Takım atanmamış'}</p>
                    <p className="text-xs text-slate-500">{agent.email}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-0.5 border text-xs font-medium inline-flex items-center gap-1 ${getStatusColor(agent.status)}`}>
                    {getStatusIcon(agent.status)}
                    <span>{getStatusLabel(agent.status)}</span>
                  </span>

                  <div className="relative group">
                    <button className="p-1 hover:bg-slate-100">
                      <MoreVertical className="w-4 h-4 text-slate-400" />
                    </button>

                    {/* Dropdown Menu */}
                    <div className="absolute right-0 top-8 w-48 bg-white border border-slate-200 shadow-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                      <div className="p-1">
                        <button
                          onClick={() => {
                            setSelectedAgent(agent);
                            setShowEditAgent(true);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center space-x-2"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Düzenle</span>
                        </button>
                        <button
                          onClick={() => resetAgentPassword(agent.id)}
                          className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center space-x-2"
                        >
                          <Key className="w-4 h-4" />
                          <span>Şifre Sıfırla</span>
                        </button>
                        <button
                          onClick={() => toggleAgentStatus(agent.id, agent.status === 'offline' ? 'available' : 'offline')}
                          className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center space-x-2"
                        >
                          {agent.status === 'offline' ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                          <span>{agent.status === 'offline' ? 'Aktifleştir' : 'Pasifleştir'}</span>
                        </button>
                        <div className="border-t border-slate-200 my-1" />
                        <button
                          onClick={() => deleteAgent(agent.id)}
                          className="w-full px-3 py-2 text-left text-sm text-danger-600 hover:bg-danger-50 flex items-center space-x-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Sil</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Agent Info - Admin görür tüm bilgileri */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-slate-600">
                  <Phone className="w-4 h-4 mr-2" />
                  <span>Dahili: {agent.extension} | Tel: {agent.phone}</span>
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <Clock className="w-4 h-4 mr-2" />
                  <span>Vardiya: {agent.shift_start} - {agent.shift_end}</span>
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>İşe Başlama: {agent.hire_date ? formatDate(agent.hire_date) : '—'}</span>
                </div>
              </div>

              {/* Performance Metrics */}
              {performances[agent.id]?.stats && (
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Bugünkü Aramalar</span>
                    <span className={`font-semibold tabular-nums ${getPerformanceColor(performances[agent.id]!.stats.total_calls_today, performance.targets.daily_calls)}`}>
                      {performances[agent.id]!.stats.total_calls_today}/{performance.targets.daily_calls}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Müşteri Memnuniyeti</span>
                    <span className={`font-semibold tabular-nums ${getPerformanceColor(performances[agent.id]!.stats.customer_satisfaction, performance.targets.satisfaction_target)}`}>
                      {performances[agent.id]!.stats.customer_satisfaction}/5 ⭐
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">İlk Çağrı Çözümü</span>
                    <span className={`font-semibold tabular-nums ${getPerformanceColor(performances[agent.id]!.stats.first_call_resolution, performance.targets.resolution_target, true)}`}>
                      %{performances[agent.id]!.stats.first_call_resolution}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Toplam Konuşma</span>
                    <span className="font-semibold text-slate-900 tabular-nums">{formatTime(performances[agent.id]!.stats.total_talk_time)}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Başarılı/Kaçırılan</span>
                    <span className="font-semibold text-slate-900 tabular-nums">{performances[agent.id]!.stats.successful_calls}/{performances[agent.id]!.stats.missed_calls}</span>
                  </div>
                </div>
              )}

              {/* Skills */}
              <div className="mb-4">
                <p className="text-sm text-slate-600 mb-2">Yetenekler:</p>
                <div className="flex flex-wrap gap-1">
                  {agent.skills?.slice(0, 2).map((skill, index) => (
                    <span key={index} className="px-2 py-0.5 bg-primary-50 text-primary-700 border border-primary-200 text-xs font-medium">
                      {skill}
                    </span>
                  ))}
                  {agent.skills && agent.skills.length > 2 && (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 text-xs font-medium">
                      +{agent.skills.length - 2}
                    </span>
                  )}
                </div>
              </div>

              {/* Achievement Badges */}
              {performance?.achievements && performance.achievements.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-slate-600 mb-2">Başarılar:</p>
                  <div className="flex flex-wrap gap-1">
                    {performance.achievements.slice(0, 2).map((achievement, index) => (
                      <span key={index} className="px-2 py-0.5 bg-yellow-50 text-yellow-700 border border-yellow-200 text-xs font-medium flex items-center">
                        <Award className="w-3 h-3 mr-1" />
                        {achievement}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setSelectedAgent(agent);
                    setShowPerformanceModal(true);
                  }}
                  className="flex-1 px-3 py-2 bg-primary-50 text-primary-700 border border-primary-200 hover:bg-primary-100 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <BarChart3 className="w-4 h-4" />
                  <span>Performans</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedAgent(agent);
                    setShowEditAgent(true);
                  }}
                  className="px-3 py-2 bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>

                <button
                  onClick={() => resetAgentPassword(agent.id)}
                  className="px-3 py-2 bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100 transition-colors"
                >
                  <Key className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Results summary */}
      {searchTerm || statusFilter !== 'all' || teamFilter !== 'all' ? (
        <div className="text-center text-slate-600">
          <p>{filteredAgents.length} Personel bulundu</p>
        </div>
      ) : null}

      {/* Performance Modal & Add/Edit Agent Modals would go here */}
      {/* These would be implemented similar to the original but with admin-specific features */}

      {/* Create Agent Modal */}
      {showAddAgent && (
        <div className="fixed inset-0 bg-primary-950/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 border-t-2 border-t-primary-600 w-full max-w-md mx-4">
            <h3 className="text-lg font-serif font-medium text-slate-900 mb-4">Yeni Personel Oluştur</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              createAgent({
                email: formData.get('email') as string,
                password: formData.get('password') as string,
                first_name: formData.get('first_name') as string,
                last_name: formData.get('last_name') as string,
                phone: formData.get('phone') as string || undefined
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className={inputCls + " w-full"}
                  />
                </div>
                <div>
                  <label className={labelCls}>Şifre</label>
                  <input
                    type="password"
                    name="password"
                    required
                    placeholder="En az 8 karakter, büyük+küçük harf, rakam, özel karakter"
                    className={inputCls + " w-full"}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Şifre gereksinimleri: 8+ karakter, büyük harf (A-Z), küçük harf (a-z), rakam (0-9), özel karakter (!@#$)
                  </p>
                </div>
                <div>
                  <label className={labelCls}>Ad</label>
                  <input
                    type="text"
                    name="first_name"
                    required
                    className={inputCls + " w-full"}
                  />
                </div>
                <div>
                  <label className={labelCls}>Soyad</label>
                  <input
                    type="text"
                    name="last_name"
                    required
                    className={inputCls + " w-full"}
                  />
                </div>
                <div>
                  <label className={labelCls}>Telefon (Opsiyonel)</label>
                  <input
                    type="tel"
                    name="phone"
                    className={inputCls + " w-full"}
                  />
                </div>
                <div className="bg-primary-50 border border-primary-200 p-3">
                  <p className="text-sm text-primary-700">
                    <strong>Not:</strong> Bu personel otomatik olarak Call Center Agent rolü ile oluşturulacaktır.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddAgent(false)}
                  disabled={isCreating}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={isCreating}
                  className="bg-primary-600 hover:bg-primary-700 text-white"
                >
                  {isCreating ? 'Oluşturuluyor...' : 'Personel Oluştur'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Performance Modal */}
      {showPerformanceModal && selectedAgent && (
        <div className="fixed inset-0 bg-primary-950/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 border-t-2 border-t-primary-600 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-serif font-medium text-slate-900">Personel Performansı - {selectedAgent.name}</h3>
              <button
                onClick={() => setShowPerformanceModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Performance Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-primary-50 border border-primary-200 p-4 text-center">
                  <p className="text-2xl font-serif font-medium text-primary-600 tabular-nums">{selectedAgent.calls_today}</p>
                  <p className="text-sm text-slate-600">Bugünkü Aramalar</p>
                </div>
                <div className="bg-success-50 border border-success-200 p-4 text-center">
                  <p className="text-2xl font-serif font-medium text-success-700 tabular-nums">{selectedAgent.rating}/5</p>
                  <p className="text-sm text-slate-600">Müşteri Memnuniyeti</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 p-4 text-center">
                  <p className="text-2xl font-serif font-medium text-yellow-700 tabular-nums">{selectedAgent.avg_call_duration}</p>
                  <p className="text-sm text-slate-600">Ortalama Süre</p>
                </div>
                <div className="bg-gold-50 border border-gold-100 p-4 text-center">
                  <p className="text-2xl font-serif font-medium text-gold-700 tabular-nums">{performances[selectedAgent.id]?.stats.successful_calls ?? 0}</p>
                  <p className="text-sm text-slate-600">Başarılı Aramalar</p>
                </div>
              </div>

              {/* Department & Contact Info */}
              <div className="bg-slate-50 border border-slate-200 p-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">İletişim Bilgileri</h4>
                <p className="text-sm text-slate-900"><strong>Email:</strong> {selectedAgent.email}</p>
                <p className="text-sm text-slate-900"><strong>Telefon:</strong> {selectedAgent.phone}</p>
                <p className="text-sm text-slate-900"><strong>Dahili:</strong> {selectedAgent.extension}</p>
                <p className="text-sm text-slate-900"><strong>Departman:</strong> {selectedAgent.department}</p>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowPerformanceModal(false)}
                className={ghostBtnCls}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Agent Modal */}
      {showEditAgent && selectedAgent && (
        <div className="fixed inset-0 bg-primary-950/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 border-t-2 border-t-primary-600 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-serif font-medium text-slate-900">Personel Düzenle - {selectedAgent.name}</h3>
              <button
                onClick={() => setShowEditAgent(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              try {
                await userApi.update(parseInt(selectedAgent.id), {
                  phone: (formData.get('phone') as string) || undefined,
                  team_id: formData.get('team_id') ? parseInt(formData.get('team_id') as string) : undefined,
                } as any);
                toast.success('Personel bilgileri güncellendi');
                await fetchAgentData();
              } catch (err: any) {
                toast.error(err?.message || 'Güncelleme başarısız');
              }
              setShowEditAgent(false);
            }}>
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Email</label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={selectedAgent.email}
                    required
                    className={inputCls + " w-full"}
                  />
                </div>
                <div>
                  <label className={labelCls}>Telefon</label>
                  <input
                    type="tel"
                    name="phone"
                    defaultValue={selectedAgent.phone || ''}
                    className={inputCls + " w-full"}
                  />
                </div>
                <div>
                  <label className={labelCls}>Dahili</label>
                  <input
                    type="text"
                    name="extension"
                    defaultValue={selectedAgent.extension}
                    className={inputCls + " w-full"}
                  />
                </div>
                <div>
                  <label className={labelCls}>Takım</label>
                  <select
                    name="team_id"
                    defaultValue={selectedAgent.team_id ? String(selectedAgent.team_id) : ''}
                    className={inputCls + " w-full"}
                  >
                    <option value="">Takım atama</option>
                    {teams.map(t => (
                      <option key={t.id} value={String(t.id)}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditAgent(false)}
                  className={ghostBtnCls}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
                >
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentManagement;
